import {
  createContext,
  ElementType,
  PropsWithChildren,
  useContext,
  useState,
} from 'react';

export interface TabItem {
  path: string;
  label: string;
  icon?: ElementType;
  hidden?: boolean;
}

interface TabLayoutContextValues {
  tabLinks: TabItem[];
  basePath: string;
  rerender: boolean;
  currentNavTab?: TabItem;
  nextPath?: string;
  currentScreen?: string;
}

interface TabLayoutProviderValues extends TabLayoutContextValues {
  updateTabLayoutContextValues: (
    newValues: Partial<TabLayoutContextValues>,
  ) => void;
  updateCurrentScreen: (screenName: string) => void;
  rerenderTabLayout: (rerender: boolean) => void;
}

const TabLayoutContext = createContext<TabLayoutProviderValues | undefined>(
  undefined,
);

export function useTabLayoutContext() {
  const context = useContext(TabLayoutContext);
  if (!context) {
    throw new Error(
      'useTabLayoutContext must be used within a TabLayoutContextProvider',
    );
  }
  return context;
}

export function TabLayoutContextProvider({ children }: PropsWithChildren) {
  const [values, setValues] = useState<TabLayoutContextValues>({
    tabLinks: [],
    basePath: '/',
    rerender: false,
  });

  const updateTabLayoutContextValues = (
    newValues: Partial<TabLayoutContextValues>,
  ) => {
    setValues((v) => ({ ...v, ...newValues }));
  };

  const updateCurrentScreen = (newScreenName: string) => {
    setValues((v) => ({
      ...v,
      currentScreen: newScreenName,
      nextPath: v.currentScreen,
    }));
  };

  const rerenderTabLayout = (rerender: boolean) => {
    setValues((v) => ({
      ...v,
      rerender,
    }));
  };

  return (
    <TabLayoutContext.Provider
      value={{
        ...values,
        updateTabLayoutContextValues,
        updateCurrentScreen,
        rerenderTabLayout,
      }}
    >
      {children}
    </TabLayoutContext.Provider>
  );
}

export function StubTabLayoutContextProvider({
  children,
  updateCurrentScreen = () => {},
}: PropsWithChildren<{ updateCurrentScreen?: (screenName: string) => void }>) {
  const value: TabLayoutProviderValues = {
    tabLinks: [],
    basePath: '/',
    rerender: false,
    updateTabLayoutContextValues: () => {},
    updateCurrentScreen,
    rerenderTabLayout: () => {},
  };
  return (
    <TabLayoutContext.Provider value={value}>
      {children}
    </TabLayoutContext.Provider>
  );
}
